import { useEffect, useState } from "react";
import { Mic2, ChevronDown } from "lucide-react";

// Lyrics panel: static full text from LRCLIB (free, no key, CORS-open).
const stripLRC = (text) =>
  (text || "")
    .split(/\r?\n/)
    .map((l) => l.replace(/^(\s*\[\d{1,2}:\d{2}(?:\.\d{1,3})?\])+/, "").trim())
    .join("\n")
    .trim();

function parseArtistTitle(raw) {
  const s = (raw || "").trim();
  if (s.includes(" - ")) {
    const [left, ...rest] = s.split(" - ");
    const right = rest.join(" - ").trim();
    if (left && right) {
      return left.length <= right.length
        ? { artist: left.trim(), title: right }
        : { artist: right, title: left.trim() };
    }
  }
  return { artist: "", title: s };
}

const hasText = (x) => x && (x.plainLyrics || x.syncedLyrics);

const fetchLyrics = async (raw, artist, title, signal) => {
  const get = async (a, t) => {
    const r = await fetch(`https://lrclib.net/api/get?track_name=${encodeURIComponent(t)}&artist_name=${encodeURIComponent(a)}`, { signal });
    return r.ok ? r.json() : null;
  };
  if (artist) {
    const j = (await get(artist, title)) || (await get(title, artist));
    if (hasText(j)) return j;
  }
  const s = await fetch(`https://lrclib.net/api/search?q=${encodeURIComponent(raw.replace(" - ", " "))}`, { signal });
  const arr = s.ok ? await s.json() : [];
  return Array.isArray(arr) ? arr.find(hasText) || null : null;
};

export default function Lyrics() {
  const [raw, setRaw] = useState("");
  const [data, setData] = useState({ status: "idle", text: "" });
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const onNow = (ev) => {
      const t = (ev?.detail?.title || "").trim();
      if (t) setRaw(t);
    };
    window.addEventListener("rrz:now-playing", onNow);
    return () => window.removeEventListener("rrz:now-playing", onNow);
  }, []);

  useEffect(() => {
    if (!raw) return;
    const { artist, title } = parseArtistTitle(raw);
    setData({ status: "loading", text: "" });
    const ac = new AbortController();
    fetchLyrics(raw, artist, title, ac.signal)
      .then((j) => {
        const text = j ? (j.plainLyrics || stripLRC(j.syncedLyrics)).trim() : "";
        setData({ status: text ? "ok" : "none", text });
      })
      .catch((e) => { if (e.name !== "AbortError") setData({ status: "none", text: "" }); });
    return () => ac.abort();
  }, [raw]);

  if (!raw) return null;

  return (
    <section id="testo" data-testid="lyrics-section" className="max-w-6xl mx-auto px-5 sm:px-8 pb-6 sm:pb-8 border-b border-[#332f2b]">
      <div className="rounded-2xl border border-[#332f2b] bg-[#141210] overflow-hidden">
        <button
          type="button"
          data-testid="lyrics-toggle"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 text-left hover:opacity-90 transition-opacity"
          aria-expanded={open}
          aria-controls="lyrics-panel"
        >
          <span className="shrink-0 w-9 h-9 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/40 flex items-center justify-center text-[#FF6B00]">
            <Mic2 size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-anton uppercase text-base sm:text-lg leading-none tracking-wide text-[#F5F2EB]">Testo in onda</h3>
            <p data-testid="lyrics-track" className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#A39A8F] mt-1 truncate">
              {raw.replace(" - ", " · ")}
            </p>
          </div>
          <ChevronDown size={18} className={`shrink-0 text-[#A39A8F] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div id="lyrics-panel" className="px-4 sm:px-8 pb-6 pt-2 border-t border-[#332f2b]/60">
            {data.status === "ok" && (
              <div
                data-testid="lyrics-text"
                className="max-h-[60vh] overflow-y-auto overscroll-contain pt-4 whitespace-pre-line text-center font-oswald text-lg sm:text-xl leading-relaxed text-[#F5F2EB]"
              >
                {data.text}
              </div>
            )}
            {data.status === "none" && (
              <p data-testid="lyrics-none" className="text-center font-mono text-xs uppercase tracking-[0.2em] text-[#A39A8F] py-6">
                Nessun testo disponibile per questo brano
              </p>
            )}
            {data.status === "loading" && (
              <p data-testid="lyrics-loading" className="text-center font-mono text-xs uppercase tracking-[0.2em] text-[#A39A8F] py-6 animate-pulse">
                Caricamento testo…
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
